import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useLoading } from '../../contexts/LoadingContext';
import apiClient from '../../utils/apiClient';
import ErrorMessage from '../common/ErrorMessage';
import LoadingButton from '../common/LoadingButton';
import Header from '../common/Header';

const Redemption = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    
    // Helper to get CID from URL case-insensitively
    const getCidFromUrl = () => {
        for (let [key, value] of searchParams.entries()) {
            if (key.toLowerCase() === 'cid') {
                return value;
            }
        }
        return '';
    };
    
    const [formData, setFormData] = useState({
        cid: getCidFromUrl(),
        venue_name: '',
        address: '',
        website: '',
        is_public: null
    });
    const [error, setError] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [cidValid, setCidValid] = useState(null);
    const [userData, setUserData] = useState(null);
    const { startLoading, stopLoading } = useLoading();

    const addressInputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const [mapsLoaded, setMapsLoaded] = useState(false);

    // Effect 1: load the script once
    useEffect(() => {
        if (window.google?.maps?.places) {
            setMapsLoaded(true);
            return;
        }
        const existingScript = document.getElementById('google-maps-script');
        if (existingScript) {
            existingScript.addEventListener('load', () => setMapsLoaded(true));
            return;
        }
        const script = document.createElement('script');
        script.id = 'google-maps-script';
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.onload = () => setMapsLoaded(true);
        document.head.appendChild(script);
    }, []);

    // Effect 2: attach autocomplete once maps is loaded AND address input is rendered
    useEffect(() => {
        if (!mapsLoaded || !addressInputRef.current) return;
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
            addressInputRef.current,
            { types: ['address'] }
        );
        autocompleteRef.current.addListener('place_changed', () => {
            const place = autocompleteRef.current.getPlace();
            if (place.formatted_address) {
                setFormData(prev => ({ ...prev, address: place.formatted_address }));
            }
        });
    }, [mapsLoaded, formData.is_public]);

    // Fetch user data to get is_staff status
    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const response = await apiClient.get('/api/users/profile/');
                setUserData(response.data);
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        };
        fetchUserData();
    }, []);

    // Auto-validate CID if it comes from URL (instant, no debounce)
    useEffect(() => {
        const cidFromUrl = getCidFromUrl();
        if (cidFromUrl && formData.cid && cidValid === null) {
            // Instant validation for QR code scans
            validateCid(formData.cid);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const validateCid = async (cid) => {
        if (!cid.trim()) {
            setCidValid(null);
            return;
        }

        setIsValidating(true);
        setError('');
        
        try {
            await apiClient.get(`/api/machine/redemption/?cid=${cid}`);
            setCidValid(true);
        } catch (error) {
            setCidValid(false);
            if (error.response?.status === 404) {
                setError('Invalid registration code. Please check and try again.');
            } else if (error.response?.status === 400) {
                setError(error.response?.data?.error || 'This machine has already been registered.');
            } else {
                setError('Unable to validate registration code. Please try again.');
            }
        } finally {
            setIsValidating(false);
        }
    };

    const handleCidChange = (e) => {
        const newCid = e.target.value.trim();
        setFormData(prev => ({ ...prev, cid: newCid }));
        setCidValid(null);
        setError('');
        
        // Only validate when user has entered a complete code (23 characters: XXXXX-XXXXXXXXX-XXXXXXX)
        if (newCid.trim().length === 23) {
            validateCid(newCid);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!cidValid) {
            setError('Please enter a valid registration code.');
            return;
        }

        if (!formData.venue_name.trim()) {
            setError('Venue name is required.');
            return;
        }

        if (!formData.address.trim()) {
            setError('Address is required.');
            return;
        }

        if (formData.is_public === null) {
            setError('Please specify whether this is a public or private location.');
            return;
        }

        startLoading('redemption');
        
        try {
            await apiClient.post('/api/machine/redemption/', {
                cid: formData.cid,
                venue_name: formData.venue_name.trim(),
                address: formData.address.trim(),
                website: formData.website.trim() || null,
                is_public: formData.is_public
            });

            navigate('/redemption/success');
            
        } catch (error) {
            const errorData = error.response?.data;
            if (errorData?.error) {
                setError(errorData.error);
            } else if (errorData?.detail) {
                setError(errorData.detail);
            } else {
                setError('Registration failed. Please try again.');
            }
        } finally {
            stopLoading('redemption');
        }
    };

    const isFromQR = getCidFromUrl();

    const handleLogout = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        navigate('/login');
    };

    const handleEditProfile = () => {
        navigate('/profile', { state: { openEditModal: true } });
    };

    return (
        <div className="pt-8 px-3">
            <Header 
                onEditProfile={handleEditProfile}
                onLogout={handleLogout}
                isStaff={userData?.is_staff}
            />
            
            <div className="max-w-md w-full mx-auto">
            <div className="text-center">
                <h1>Register your Elite</h1>
            </div>

            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>

                {/* Step 1: Redemption code */}
                <div>
                    <label htmlFor="cid" className="block text-sm font-bold text-gray-700 mb-2">
                        Enter Code:
                    </label>
                    <input
                        id="cid"
                        name="cid"
                        type="text"
                        className={`appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm ${isFromQR ? 'bg-gray-100' : ''}`}
                        placeholder="Registration Code*"
                        value={formData.cid}
                        onChange={handleCidChange}
                        disabled={isValidating}
                        readOnly={isFromQR}
                    />
                    {isValidating && (
                        <p className="mt-1 text-sm text-gray-500">Validating code...</p>
                    )}
                    {cidValid === true && (
                        <p className="mt-1 text-sm text-green-600">✓ Valid registration code</p>
                    )}
                </div>

                {/* Step 2: Public/private — appears once code is valid */}
                {cidValid === true && (
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-3">
                            Will this machine be available for public play?*
                        </label>
                        <div className="flex gap-6">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="is_public"
                                    checked={formData.is_public === true}
                                    onChange={() => setFormData(prev => ({ ...prev, is_public: true, venue_name: '', address: '', website: '' }))}
                                    className="accent-popashot-blue"
                                />
                                <span className="text-sm text-gray-900 font-medium">Yes</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="is_public"
                                    checked={formData.is_public === false}
                                    onChange={() => setFormData(prev => ({ ...prev, is_public: false, venue_name: '', address: '', website: '' }))}
                                    className="accent-popashot-blue"
                                />
                                <span className="text-sm text-gray-900 font-medium">No</span>
                            </label>
                        </div>
                    </div>
                )}

                {/* Step 3: Location fields — conditional on public/private selection */}
                {formData.is_public === true && (
                    <div className="rounded-md shadow-sm -space-y-px">
                        <input
                            id="venue_name"
                            name="venue_name"
                            type="text"
                            className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                            placeholder="Venue Name*"
                            value={formData.venue_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, venue_name: e.target.value }))}
                        />
                        <input
                            ref={addressInputRef}
                            id="address"
                            name="address"
                            type="text"
                            className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                            placeholder="Address*"
                            value={formData.address}
                            onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                        />
                        <input
                            id="website"
                            name="website"
                            type="text"
                            className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                            placeholder="Website (optional)"
                            value={formData.website}
                            onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                        />
                    </div>
                )}

                {formData.is_public === false && (
                    <div>
                        <div className="rounded-md shadow-sm -space-y-px">
                            <input
                                id="venue_name"
                                name="venue_name"
                                type="text"
                                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                placeholder="Machine Name*"
                                value={formData.venue_name}
                                onChange={(e) => setFormData(prev => ({ ...prev, venue_name: e.target.value }))}
                            />
                            <input
                                ref={addressInputRef}
                                id="address"
                                name="address"
                                type="text"
                                className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                                placeholder="Address*"
                                value={formData.address}
                                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                            />
                        </div>
                        <p className="mt-1 text-xs text-gray-400">Address is used for warranty &amp; support purposes only.</p>
                    </div>
                )}

                <ErrorMessage error={error} />

                <LoadingButton
                    type="submit"
                    loadingKey="redemption"
                    className="btn-primary w-full"
                    disabled={isValidating}
                >
                    {isValidating ? 'Validating...' : 'Register'}
                </LoadingButton>
            </form>
            </div>
        </div>
    );
};

export default Redemption;
